import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Server as ServerIcon,
  PlusIcon,
  TrashIcon,
  ExternalLinkIcon,
  CopyIcon,
  RotateCcwIcon,
  Trash2Icon
} from 'lucide-react';
import { useUser } from '@/context/user-provider';
import { toast } from 'sonner';
import { organizationService } from '@/services/organization-service';
import { assetService } from '@/services/asset-service';
import { serviceManagementService, type AssetService, type AssetServiceWithDetails } from '@/services/service-management-service';

interface Asset {
  uuid: string;
  name: string;
  type: string;
  createdAt: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
}

// Use AssetServiceWithDetails directly from the service
type Service = AssetServiceWithDetails;

const ServiceManagementPanel = () => {
  const { user, activeOrganization } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState<Service[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Form state
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const [serviceSlug, setServiceSlug] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlAssetId, setUrlAssetId] = useState<string | null>(null);

  // Check if we have an assetId in the URL params to pre-select
  // Use a separate state to track URL-driven opening
  useEffect(() => {
    const assetId = searchParams.get('assetId');

    // Only process if the URL actually has an assetId
    if (assetId && assetId !== urlAssetId) {
      setUrlAssetId(assetId);
      setSelectedAsset(assetId);
      setIsCreateDialogOpen(true);

      // If user only belongs to one organization, pre-select it
      if (organizations.length === 1) {
        setSelectedOrg(organizations[0].id);
      }
    }
    // If URL no longer has assetId but we stored one, it means it was cleared
    else if (!assetId && urlAssetId) {
      setUrlAssetId(null);
      // Dialog should already be closed by handleDialogOpenChange
    }
  }, [searchParams, organizations, urlAssetId]);

  // Fetch services, assets, and organizations
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch services for the active organization only
        const servicesData = activeOrganization?.organizationId
          ? await serviceManagementService.listServices(activeOrganization.organizationId)
          : [];
        setServices(servicesData);
        
        // Fetch user's assets
        const assetsData = await assetService.listAssets();
        setAssets(assetsData.data || []);
        
        // Fetch user's organizations
        const orgsData = await organizationService.list();
        setOrganizations(orgsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load services and related data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshTrigger, toast, activeOrganization]);

  const handleCreateService = async () => {
    if (!selectedAsset || !serviceSlug || !selectedOrg) {
      toast.error('Please select an asset, enter a service slug, and select an organization');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use the service management service to create the service
      await serviceManagementService.createService(selectedAsset, {
        serviceSlug,
        organizationId: selectedOrg
      });

      toast.success('Service created successfully!');

      // Reset form
      setSelectedAsset('');
      setServiceSlug('');
      setSelectedOrg('');

      // Close dialog
      setIsCreateDialogOpen(false);

      // Refresh the list
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create service');
      // Dialog stays open on error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivateService = async (serviceId: string) => {
    try {
      // Use the service management service to unpublish the service
      await serviceManagementService.unpublishService(serviceId);

      toast.success('Service unpublished successfully!');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete service',
        variant: 'destructive'
      });
    }
  };

  const handleReactivateService = async (serviceId: string) => {
    try {
      await serviceManagementService.reactivateService(serviceId);
      toast.success('Service reactivated successfully!');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error reactivating service:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to reactivate service',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveService = async (serviceId: string) => {
    try {
      await serviceManagementService.removeService(serviceId);
      toast.success('Service removed permanently');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error removing service:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove service',
        variant: 'destructive'
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Service URL copied to clipboard');
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open);

    // When closing the dialog, clear the URL parameter and form state
    if (!open) {
      // Clear the URL asset ID tracking to prevent useEffect from reopening
      setUrlAssetId(null);

      // Clear the assetId URL parameter
      navigate('/console/services', { replace: true });

      // Clear form state
      setSelectedAsset('');
      setServiceSlug('');
      setSelectedOrg('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Service Management</h2>
          <p className="text-muted-foreground">
            Publish your assets as services and manage existing services
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogTrigger asChild>
            <Button>
              <PlusIcon className="mr-2 h-4 w-4" />
              Create Service
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Service</DialogTitle>
              <DialogDescription>
                Publish an asset as a service that others can access via a URL
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="asset">Asset</Label>
                <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an asset" />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map(asset => (
                      <SelectItem key={asset.uuid} value={asset.uuid}>
                        {asset.name} ({asset.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="serviceSlug">Service Slug</Label>
                <Input
                  id="serviceSlug"
                  value={serviceSlug}
                  onChange={(e) => setServiceSlug(e.target.value)}
                  placeholder="e.g., my-public-model"
                />
                <p className="text-xs text-muted-foreground">
                  URL-friendly identifier for your service (alphanumeric, hyphens, underscores)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="context">Organization</Label>
                <Select value={selectedOrg} onValueChange={setSelectedOrg} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Services can only be published to organizations. You must be a member of the organization to publish services.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => handleDialogOpenChange(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateService} 
                disabled={isSubmitting || !selectedAsset || !serviceSlug}
              >
                {isSubmitting ? 'Creating...' : 'Create Service'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Published Services</CardTitle>
        </CardHeader>
        <CardContent>
          {services.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ServerIcon className="mx-auto h-12 w-12 mb-4" />
              <h3 className="text-lg font-medium mb-2">No services yet</h3>
              <p>Start by creating your first service from one of your assets.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Service URL</TableHead>
                  <TableHead>Context</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map(service => (
                  <TableRow key={service.id}>
                    <TableCell className="font-medium">
                      {service.asset?.name || 'Unknown Asset'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          /api/svc/
                          {(service.organization && service.organization.slug) 
                            ? service.organization.slug 
                            : user?.name || 'me'}/
                          {service.serviceSlug}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(
                            `${window.location.origin}/api/svc/${(service.organization && service.organization.slug) 
                              ? service.organization.slug 
                              : user?.name || 'me'}/${service.serviceSlug}`
                          )}
                          className="ml-2 h-8 w-8 p-0"
                        >
                          <CopyIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {service.organization && service.organization.name ? (
                        <Badge variant="secondary">
                          {service.organization.name}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          Personal
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={service.isActive ? "default" : "destructive"}>
                        {service.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/console/services/${service.id}`)}
                        >
                          Manage
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/console/services/${service.id}/test`)}
                        >
                          <ExternalLinkIcon className="h-4 w-4 mr-2" />
                          Test
                        </Button>
                        {service.isActive ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeactivateService(service.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <TrashIcon className="h-4 w-4 mr-2" />
                            Deactivate
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReactivateService(service.id)}
                            >
                              <RotateCcwIcon className="h-4 w-4 mr-2" />
                              Reactivate
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRemoveService(service.id)}
                            >
                              <Trash2Icon className="h-4 w-4 mr-2" />
                              Delete Forever
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceManagementPanel;
